import { ErrorAlert, ReturnsTableWithTabs } from "@secondcloset/web-components";
import { Spin, Typography } from "antd";
import React, { FC, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router";
import { useOrganizationContext } from "../../../contextProviders/organization/OrganizationProvider";
// Hooks
import { fetchReturns } from "../../../hooks/api/warehouse/returns";
import useUrlState from "../../../hooks/application/useUrlState";
//Helpers
import { ReturnStatus } from "./helpers";

/* eslint-disable react-hooks/exhaustive-deps */

const statusConfig = [
  {
    title: "All",
    key: "ALL",
    return_statuses: [...Object.keys(ReturnStatus)],
  },
  {
    title: "Open",
    key: "OPEN",
    return_statuses: [
      ReturnStatus.READY_TO_PROCESS,
      ReturnStatus.ARRIVED,
      ReturnStatus.PROCESSING,
      ReturnStatus.REQUESTED,
      ReturnStatus.ON_HOLD,
    ],
  },
  {
    title: "Needs Review",
    key: "NEEDS_REVIEW",
    return_statuses: [ReturnStatus.NEEDS_REVIEW],
  },
  {
    title: "Closed",
    key: "CLOSED",
    return_statuses: [ReturnStatus.CANCELLED, ReturnStatus.COMPLETED, ReturnStatus.TO_BE_RESHIPPED],
  },
];

const ReturnsIndexPage: FC = () => {
  const { data: organizationDetails } = useOrganizationContext();
  const history = useHistory();
  const [tabFilter, setTabFilter] = useUrlState({
    name: "state",
    initialValue: "",
  });
  const [status, setStatus] = useUrlState({ name: "status", initialValue: "" });
  const [search, setSearch] = useUrlState({ name: "search", initialValue: "" });
  const [page, setPage] = useUrlState({ name: "page", initialValue: 1 });
  const [perPage, setPerPage] = useUrlState({
    name: "perPage",
    initialValue: 10,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState("");

  const query = {
    organization_id: organizationDetails?.id,
    tab: tabFilter?.toUpperCase() === "ALL" || tabFilter === "" ? undefined : tabFilter,
    status: status || undefined,
    search: search || undefined,
    limit: (perPage || 10).toString(),
    page: (page || 1).toString(),
  };

  const paginatedReturns = useQuery(
    ["fetchReturns", search, tabFilter, status, page, perPage],
    () =>
      fetchReturns({
        organization_id: organizationDetails?.id,
        tab: tabFilter?.toUpperCase() === "ALL" || tabFilter === "" ? undefined : tabFilter,
        status: status || undefined,
        search: search || undefined,
        limit: (perPage || 10).toString(),
        page: (page || 1).toString(),
      }),
    {
      enabled: !!organizationDetails?.id,
      onSuccess: () => {
        setError("");
      },
      onError: (error: string) => setError(error),
    }
  );

  useEffect(() => {
    if (organizationDetails?.id) {
      fetchReturns(query);
    }
  }, [organizationDetails, page, tabFilter, search, status, perPage]);

  const handleSearch = (e: any) => {
    setPage(1);
    setSearch(e?.target?.value?.trim());
  };

  const handleTabChange = (key: string) => {
    setShowFilters(false);
    setStatus("");
    setPage(1);
    setPerPage(10);
    const tabStatusFilter = statusConfig.find((config) => config?.key?.toUpperCase() === key?.toUpperCase())?.key;
    setTabFilter(tabStatusFilter || "");
  };

  const handleSetStatus = (status?: string) => {
    const modifiedStatus = status?.toLowerCase() === "all" ? null : status;
    setStatus(modifiedStatus || "");
  };

  return (
    <div className="page-container" id="returns-index-page">
      <Spin spinning={paginatedReturns?.isLoading}>
        <ErrorAlert error={error} />
        <Typography.Title level={4}>Returns</Typography.Title>
        <ReturnsTableWithTabs
          activeTab={tabFilter || "ALL"}
          setPage={setPage}
          onPageSizeChange={setPerPage}
          statusConfig={statusConfig}
          setStatus={handleSetStatus}
          setShowFilters={(show?: boolean) => setShowFilters(show || false)}
          showFilters={showFilters}
          handleNavigation={(id: string) => history.push(`/organizations/${organizationDetails?.id}/returns/${id}`)}
          handleSearch={handleSearch}
          handleTabChange={handleTabChange}
          returns={paginatedReturns?.data || undefined}
        />
      </Spin>
    </div>
  );
};

export default ReturnsIndexPage;
